// src/pages/estoque/ImportarXMLPage.js
import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCheck, FaExclamationTriangle, FaFileCode, FaLink, FaPlus, FaSearch, FaTimes, FaUpload } from 'react-icons/fa';
import PageContainer from '../../components/geral/PageContainer';
import PageContent from '../../components/geral/PageContent';
import PageFooter from '../../components/geral/PageFooter';
import Input from '../../components/input/Input';
import Select from '../../components/input/Select';
import { useFornecedor } from '../../contexts/pessoas/FornecedorContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { useCidades } from '../../contexts/geral/CidadeContext';
import { useNatureza } from '../../contexts/vendas/NaturezaContext';
import { useProdutoFornec } from '../../contexts/produtos/ProdutoFornecContext';
import { useProduto } from '../../contexts/produtos/ProdutoContext';
import { useNota } from '../../contexts/vendas/NotaContext';
import ProdutoCad from '../produtos/produtos/ProdutoCad';
import { useModalStack } from '../../contexts/geral/ModalStackContext';
import {
  calcularTotaisImportacao,
  importacaoPossuiDivergencia,
  parseNFeXml,
} from '../../services/estoque/ImportacaoXmlService';
import { formatCurrency, formatNumeric, normalizeDecimalInputBr, parseNumeric } from '../../utils/numberUtils';
import './ImportarXMLPage.css';

const formatarDocumento = (valor) => {
  const digitos = String(valor || '').replace(/\D/g, '');
  if (digitos.length === 14) return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  if (digitos.length === 11) return digitos.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
  return valor || '-';
};

const criarFornecedorInicial = (dados, cidades) => {
  const cidade = cidades.find((item) => String(item.cMun) === String(dados.codigoMunicipio));
  return {
    pessoa: {
      nome: dados.nome,
      juridica: dados.cnpj ? { cnpj: dados.cnpj, razaoSocial: dados.nome, ie: dados.ie || '' } : null,
      fisica: dados.cpf ? { cpf: dados.cpf } : null,
      email: dados.email ? { enderecoEmail: dados.email } : null,
      telefone: dados.telefone ? { numero: dados.telefone } : null,
      endereco: {
        logradouro: dados.logradouro || '', numero: dados.numero || '', complemento: dados.complemento || '',
        bairro: dados.bairro || '', cep: dados.cep || '', cidadeId: cidade?.id || null, cidade: cidade || null,
      },
    },
  };
};

const ImportarXMLPage = () => {
  const navigate = useNavigate();
  const inputArquivoRef = useRef(null);
  const { fornecedores, setFornecedorSel, showFornecedorCad } = useFornecedor();
  const { cidades } = useCidades();
  const { naturezas } = useNatureza();
  const { resolverCodigos, resolverConflito, addProdutoFornec } = useProdutoFornec();
  const { showProdutoSearch } = useProduto();
  const { addNota, buscaNotaByChave } = useNota();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();

  const [arquivoNome, setArquivoNome] = useState('');
  const [nota, setNota] = useState(null);
  const [itens, setItens] = useState([]);
  const [fornecedor, setFornecedor] = useState(null);
  const [filtro, setFiltro] = useState('todos');
  const [pesquisa, setPesquisa] = useState('');
  const [isImportando, setIsImportando] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showCadProduto, setShowCadProduto] = useState(false);
  const [produtoInicial, setProdutoInicial] = useState(null);
  const [vinculoModal, setVinculoModal] = useState(null);
  const vinculoModalLayer = useModalStack(!!vinculoModal, 1065);

  useLayoutEffect(() => {
    if (!vinculoModal) return undefined;

    const modal = document.querySelector('.importar-xml-modal');
    if (!modal) return undefined;

    const previousZIndex = modal.style.zIndex;
    modal.style.zIndex = String(vinculoModalLayer.zIndex);

    return () => {
      modal.style.zIndex = previousZIndex;
    };
  }, [vinculoModal, vinculoModalLayer.zIndex]);

  const naturezasEntrada = useMemo(
    () => naturezas.filter((item) => Number(item.tipoMov) === 1),
    [naturezas]
  );
  const totais = useMemo(() => calcularTotaisImportacao(nota, itens), [nota, itens]);
  const possuiDivergencia = useMemo(() => importacaoPossuiDivergencia(nota, itens), [nota, itens]);
  const contadores = useMemo(() => itens.reduce((acc, item) => {
    acc[item.statusVinculo] = (acc[item.statusVinculo] || 0) + 1;
    return acc;
  }, { vinculado: 0, pendente: 0, conflito: 0 }), [itens]);
  const itensFiltrados = useMemo(() => itens.filter((item) => {
    const atendeStatus = filtro === 'todos' || item.statusVinculo === filtro;
    const termo = pesquisa.trim().toLowerCase();
    const atendePesquisa = !termo || [item.cProd, item.ean, item.descricao, item.produtoDescricao, item.produtoId]
      .some((valor) => String(valor || '').toLowerCase().includes(termo));
    return atendeStatus && atendePesquisa;
  }), [itens, filtro, pesquisa]);
  const prontoParaSalvar = nota && fornecedor?.id && itens.length > 0
    && contadores.pendente === 0 && contadores.conflito === 0 && !isResolving;

  const resolverVinculos = async (itensAtuais, fornecedorAtual) => {
    if (!fornecedorAtual?.id || itensAtuais.length === 0) return itensAtuais;
    setIsResolving(true);
    try {
      const resolucoes = await resolverCodigos(
        fornecedorAtual.id,
        itensAtuais.map((item) => item.cProd).filter(Boolean)
      );
      const porCodigo = new Map((resolucoes || []).map((item) => [item.cProdFornec, item]));
      const atualizados = itensAtuais.map((item) => {
        const resolucao = porCodigo.get(item.cProd);
        if (!resolucao) return { ...item, statusVinculo: 'pendente', produtoId: null, produtoDescricao: '', candidatos: [] };
        return {
          ...item,
          statusVinculo: resolucao.status,
          produtoId: resolucao.produtoId || null,
          produtoDescricao: resolucao.produtoDescricao || '',
          candidatos: resolucao.candidatos || [],
        };
      });
      setItens(atualizados);
      return atualizados;
    } catch (error) {
      showMessage(error?.message || 'Não foi possível consultar os vínculos dos produtos.', 'danger');
      throw error;
    } finally {
      setIsResolving(false);
    }
  };

  const localizarFornecedor = (dados) => fornecedores.find((item) => {
    const cnpj = item.pessoa?.juridica?.cnpj?.replace(/\D/g, '');
    const cpf = item.pessoa?.fisica?.cpf?.replace(/\D/g, '');
    return (dados.cnpj && cnpj === dados.cnpj) || (dados.cpf && cpf === dados.cpf);
  });

  const processarArquivo = async (arquivo) => {
    if (!arquivo) return;
    if (!arquivo.name.toLowerCase().endsWith('.xml')) {
      showMessage('Selecione um arquivo com extensão .xml.', 'warning');
      return;
    }

    setIsImportando(true);
    try {
      const resultado = parseNFeXml(await arquivo.text());
      const jaImportada = resultado.nota.chave ? await buscaNotaByChave(resultado.nota.chave) : null;
      if (jaImportada) throw new Error(`Esta nota já foi importada anteriormente (ID ${jaImportada.id}).`);

      let fornecedorEncontrado = localizarFornecedor(resultado.fornecedor);
      if (!fornecedorEncontrado) {
        const desejaCadastrar = await showYesNo(
          `Fornecedor ${resultado.fornecedor.nome} não encontrado. Deseja cadastrá-lo agora?`
        );
        if (!desejaCadastrar) throw new Error('Importação cancelada porque o fornecedor não está cadastrado.');
        fornecedorEncontrado = await showFornecedorCad(criarFornecedorInicial(resultado.fornecedor, cidades));
        if (!fornecedorEncontrado) throw new Error('O cadastro do fornecedor foi cancelado.');
      }

      setFornecedor(fornecedorEncontrado);
      setFornecedorSel(fornecedorEncontrado);
      setArquivoNome(arquivo.name);
      const naturezaId = naturezasEntrada[0]?.id || '';
      const notaCarregada = { ...resultado.nota, naturezaId };
      const itensFormatados = resultado.itens.map((item) => ({
        ...item,
        quantidade: formatNumeric(item.quantidade, 2),
        valorUnit: formatNumeric(item.valorUnit, 4),
      }));
      setNota(notaCarregada);
      setItens(itensFormatados);
      setFiltro('todos');
      setPesquisa('');
      await resolverVinculos(itensFormatados, fornecedorEncontrado);
    } catch (error) {
      if (!String(error?.message || '').toLowerCase().includes('cancelad')) {
        showMessage(error?.message || 'Não foi possível importar o XML.', 'danger');
      } else {
        showMessage(error.message, 'warning');
      }
    } finally {
      setIsImportando(false);
      if (inputArquivoRef.current) inputArquivoRef.current.value = '';
    }
  };

  const alterarItem = (itemNumero, campo, valor) => {
    const normalizado = normalizeDecimalInputBr(valor, campo === 'quantidade' ? 3 : 4).replace(/-/g, '');
    setItens((atuais) => atuais.map((item) => {
      if (item.item !== itemNumero) return item;
      const atualizado = { ...item, [campo]: normalizado };
      atualizado.valorTotal = parseNumeric(atualizado.quantidade) * parseNumeric(atualizado.valorUnit);
      return atualizado;
    }));
  };

  const formatarItem = (itemNumero, campo) => {
    setItens((atuais) => atuais.map((item) => item.item === itemNumero
      ? { ...item, [campo]: formatNumeric(parseNumeric(item[campo]), campo === 'quantidade' ? 2 : 4) }
      : item));
  };

  const abrirCadastroProduto = (item) => {
    setProdutoInicial({
      descricao: item.descricao,
      ean: item.ean,
      ncm: item.ncm,
      cest: item.cest,
      cProdFornec: item.cProd,
      fornecedorId: fornecedor.id,
      fornecedor,
      custoUltimo: formatNumeric(parseNumeric(item.valorUnit), 2),
      ...item.tributacaoVenda,
    });
    setShowCadProduto(true);
  };

  const produtoCadastrado = async () => {
    setShowCadProduto(false);
    setProdutoInicial(null);
    await resolverVinculos(itens, fornecedor);
  };

  const abrirVinculo = (item, modo = 'vincular') => {
    setVinculoModal({
      modo,
      cProdFornec: item.cProd,
      produtoId: modo === 'resolver' ? item.candidatos?.[0]?.produtoId || '' : '',
      produto: modo === 'resolver' ? item.candidatos?.[0] || null : null,
      candidatos: item.candidatos || [],
    });
  };

  const buscarProdutoVinculo = async () => {
    const produto = await showProdutoSearch();
    if (produto) setVinculoModal((atual) => ({ ...atual, produtoId: produto.id, produto }));
  };

  const confirmarVinculo = async () => {
    if (!vinculoModal?.produtoId) return;
    try {
      const payload = {
        fornecedorId: fornecedor.id,
        produtoId: Number(vinculoModal.produtoId),
        cProdFornec: vinculoModal.cProdFornec,
      };
      if (vinculoModal.modo === 'resolver') {
        if (!(await showYesNo('Deseja manter este produto e remover os demais vínculos conflitantes?'))) return;
        await resolverConflito(payload);
      } else {
        await addProdutoFornec(payload);
      }
      setVinculoModal(null);
      await resolverVinculos(itens, fornecedor);
    } catch (error) {
      showMessage(error?.message || 'Não foi possível salvar o vínculo.', 'danger');
    }
  };

  const alterarValorNota = (campo, valor) => {
    setNota((atual) => ({ ...atual, [campo]: normalizeDecimalInputBr(valor, 2).replace(/-/g, '') }));
  };

  const salvarNota = async () => {
    if (!prontoParaSalvar || isSaving) return;
    if (!nota.naturezaId) return showMessage('Selecione uma natureza de entrada.', 'warning');
    if (itens.some((item) => parseNumeric(item.quantidade) <= 0)) return showMessage('Todos os itens devem possuir quantidade maior que zero.', 'warning');
    if (itens.some((item) => parseNumeric(item.valorUnit) < 0)) return showMessage('Existe item com valor unitário inválido.', 'warning');
    if (possuiDivergencia && !(await showYesNo(
      `Os valores foram alterados em relação ao XML. Total original: ${formatCurrency(nota.totaisOriginais.vNota)}. Novo total: ${formatCurrency(totais.vNota)}. Deseja continuar?`
    ))) return;

    const payload = {
      fornecedorId: fornecedor.id,
      naturezaId: Number(nota.naturezaId),
      cnpj: nota.cnpj || null,
      cpf: nota.cpf || null,
      cfop: itens[0]?.cfopEntrada || '1102',
      tipoMov: 1,
      modeloNF: nota.modeloNF || null,
      serie: nota.serie ? Number(nota.serie) : null,
      numDoc: nota.numero ? Number(nota.numero) : null,
      chave: nota.chave || null,
      vProd: totais.vProd,
      vDesc: totais.vDesc,
      vOutro: totais.vOutro,
      vFrete: totais.vFrete,
      vNota: totais.vNota,
      dataEmissao: nota.dataEmissao ? `${nota.dataEmissao}T12:00:00` : null,
      notaItens: itens.map((item, index) => ({
        item: index + 1,
        produtoId: item.produtoId,
        cfop: item.cfopEntrada || '1102',
        unCom: item.unidade || 'UN',
        qtdCom: parseNumeric(item.quantidade),
        vUnCom: parseNumeric(item.valorUnit),
        vProd: parseNumeric(item.valorTotal),
        xProd: item.descricao,
      })),
    };

    setIsSaving(true);
    try {
      const salva = await addNota(payload);
      showMessage(`Nota de entrada ${salva?.id || ''} salva com sucesso.`, 'success');
      setNota(null); setItens([]); setFornecedor(null); setArquivoNome('');
    } catch (error) {
      showMessage(error?.message || 'Não foi possível salvar a nota de entrada.', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const cancelarImportacao = async () => {
    if (nota && !(await showYesNo('Deseja descartar esta importação?'))) return;
    setNota(null); setItens([]); setFornecedor(null); setArquivoNome(''); setFiltro('todos'); setPesquisa('');
  };

  const statusBadge = (status) => {
    const labels = { vinculado: 'Vinculado', pendente: 'Pendente', conflito: 'Conflito' };
    return <span className={`importar-xml-status ${status}`}>{status === 'vinculado' ? <FaCheck /> : <FaExclamationTriangle />}{labels[status] || status}</span>;
  };

  return (
    <PageContainer>
      <div className="importar-xml-page">
        <header className="importar-xml-cabecalho">
          <div><h2 className="page-title">Importar nota fiscal</h2><span>Carregue a NF-e, revise os vínculos e conclua a entrada no estoque.</span></div>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/entrada')}>Entrada manual</button>
        </header>

        <PageContent>
          <section
            className={`importar-xml-upload ${isDragging ? 'is-dragging' : ''} ${nota ? 'has-file' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => { e.preventDefault(); setIsDragging(false); processarArquivo(e.dataTransfer.files?.[0]); }}
          >
            <input ref={inputArquivoRef} type="file" accept=".xml,text/xml,application/xml" hidden onChange={(e) => processarArquivo(e.target.files?.[0])} />
            <div className="importar-xml-upload-icon">{isImportando ? <span className="spinner-border spinner-border-sm" /> : <FaFileCode />}</div>
            <div><strong>{arquivoNome || 'Arraste o XML da NF-e para esta área'}</strong><span>{nota ? 'Arquivo carregado. Você pode substituí-lo por outro XML.' : 'ou selecione o arquivo salvo no computador'}</span></div>
            <button type="button" className="btn btn-primary" disabled={isImportando} onClick={() => inputArquivoRef.current?.click()}><FaUpload /> {nota ? 'Substituir XML' : 'Selecionar XML'}</button>
          </section>

          {nota && <>
            <section className="importar-xml-resumo-grid">
              <article className="importar-xml-card fornecedor"><div className="importar-xml-card-title"><strong>Fornecedor</strong><span className="importar-xml-ok"><FaCheck /> Identificado</span></div><h3>{fornecedor?.pessoa?.nome || nota.fornecedorNome}</h3><p>{formatarDocumento(nota.cnpj || nota.cpf)}</p></article>
              <article className="importar-xml-card documento"><div className="importar-xml-card-title"><strong>Documento</strong><span>{nota.naturezaOperacao}</span></div><div className="importar-xml-documento-dados"><span><small>NF-e</small><strong>{nota.numero || '-'}</strong></span><span><small>Série</small><strong>{nota.serie || '-'}</strong></span><span><small>Emissão</small><strong>{nota.dataEmissao ? new Date(`${nota.dataEmissao}T12:00:00`).toLocaleDateString('pt-BR') : '-'}</strong></span><span><small>Modelo</small><strong>{nota.modeloNF || '-'}</strong></span></div></article>
              <article className="importar-xml-card totais"><div className="importar-xml-card-title"><strong>Totais</strong>{possuiDivergencia && <span className="importar-xml-divergencia"><FaExclamationTriangle /> Alterado</span>}</div><div className="importar-xml-total-principal"><small>Total da entrada</small><strong>{formatCurrency(totais.vNota)}</strong></div><span>Original no XML: {formatCurrency(nota.totaisOriginais.vNota)}</span></article>
            </section>

            <section className="importar-xml-card importar-xml-config">
              <Select label="Natureza da entrada *" size="100%" value={nota.naturezaId} options={[{ value: '', label: naturezasEntrada.length ? 'Selecione' : 'Nenhuma natureza de entrada' }, ...naturezasEntrada.map((item) => ({ value: item.id, label: `${item.id} - ${item.descricao}` }))]} onChange={(e) => setNota((atual) => ({ ...atual, naturezaId: e.target.value }))} />
              <Input label="Desconto" size="100%" value={nota.vDesc} onChange={(e) => alterarValorNota('vDesc', e.target.value)} />
              <Input label="Frete" size="100%" value={nota.vFrete} onChange={(e) => alterarValorNota('vFrete', e.target.value)} />
              <Input label="Outros valores" size="100%" value={nota.vOutro} onChange={(e) => alterarValorNota('vOutro', e.target.value)} />
              <div className="importar-xml-chave"><small>Chave de acesso</small><span>{nota.chave || '-'}</span></div>
            </section>

            <section className="importar-xml-itens-card">
              <div className="importar-xml-toolbar">
                <div className="importar-xml-contadores"><button className={filtro === 'todos' ? 'active' : ''} onClick={() => setFiltro('todos')}>Todos <strong>{itens.length}</strong></button><button className={filtro === 'vinculado' ? 'active success' : 'success'} onClick={() => setFiltro('vinculado')}>Vinculados <strong>{contadores.vinculado}</strong></button><button className={filtro === 'pendente' ? 'active warning' : 'warning'} onClick={() => setFiltro('pendente')}>Pendentes <strong>{contadores.pendente}</strong></button><button className={filtro === 'conflito' ? 'active danger' : 'danger'} onClick={() => setFiltro('conflito')}>Conflitos <strong>{contadores.conflito}</strong></button></div>
                <div className="importar-xml-search"><FaSearch /><input value={pesquisa} onChange={(e) => setPesquisa(e.target.value)} placeholder="Buscar código, EAN ou produto" /></div>
              </div>
              {isResolving && <div className="importar-xml-resolving"><span className="spinner-border spinner-border-sm" /> Consultando vínculos dos produtos...</div>}
              <div className="importar-xml-table-wrap"><table className="page-content-table importar-xml-table"><thead><tr><th>#</th><th>Cód. fornecedor</th><th>EAN</th><th>Produto no XML</th><th>Produto vinculado</th><th>Un.</th><th className="text-end">Quantidade</th><th className="text-end">Valor unit.</th><th className="text-end">Total</th><th>Status</th><th>Ações</th></tr></thead><tbody>
                {itensFiltrados.length === 0 ? <tr><td colSpan="11" className="importar-xml-empty">Nenhum item corresponde ao filtro selecionado.</td></tr> : itensFiltrados.map((item) => <tr key={item.item} className={`status-${item.statusVinculo}`}><td>{item.item}</td><td className="fw-semibold">{item.cProd || '-'}</td><td>{item.ean && item.ean !== 'SEM GTIN' ? item.ean : '-'}</td><td><div className="importar-xml-produto"><strong>{item.descricao}</strong><small>NCM {item.ncm || '-'}</small></div></td><td>{item.produtoId ? <div className="importar-xml-produto"><strong>{item.produtoId} - {item.produtoDescricao || 'Produto cadastrado'}</strong><small>CFOP entrada {item.cfopEntrada}</small></div> : '-'}</td><td>{item.unidade}</td><td><input className="form-control form-control-sm text-end" value={item.quantidade} onChange={(e) => alterarItem(item.item, 'quantidade', e.target.value)} onBlur={() => formatarItem(item.item, 'quantidade')} /></td><td><input className="form-control form-control-sm text-end" value={item.valorUnit} onChange={(e) => alterarItem(item.item, 'valorUnit', e.target.value)} onBlur={() => formatarItem(item.item, 'valorUnit')} /></td><td className="text-end fw-semibold">{formatCurrency(item.valorTotal)}</td><td>{statusBadge(item.statusVinculo)}</td><td><div className="importar-xml-actions">{item.statusVinculo === 'pendente' && <><button className="btn btn-sm btn-outline-primary" onClick={() => abrirCadastroProduto(item)}><FaPlus /> Cadastrar</button><button className="btn btn-sm btn-outline-success" onClick={() => abrirVinculo(item)}><FaLink /> Vincular</button></>}{item.statusVinculo === 'conflito' && <button className="btn btn-sm btn-outline-danger" onClick={() => abrirVinculo(item, 'resolver')}><FaExclamationTriangle /> Resolver</button>}</div></td></tr>)}
              </tbody></table></div>
            </section>
          </>}
        </PageContent>

        <PageFooter><div className="importar-xml-footer-status">{nota ? <>{prontoParaSalvar ? <span className="ready"><FaCheck /> Todos os itens estão prontos</span> : <span className="pending"><FaExclamationTriangle /> Resolva {contadores.pendente + contadores.conflito} {contadores.pendente + contadores.conflito === 1 ? 'item' : 'itens'} antes de salvar</span>}<strong>{formatCurrency(totais.vNota)}</strong></> : <span>Selecione um XML para iniciar.</span>}</div><div className="botoes-footer"><button type="button" className="btn btn-outline-danger btn-footer" disabled={!nota} onClick={cancelarImportacao}><FaTimes /> Cancelar</button><button type="button" className="btn btn-primary btn-footer" disabled={!prontoParaSalvar || isSaving} onClick={salvarNota}><FaCheck /> {isSaving ? 'Salvando...' : 'Salvar entrada'}</button></div></PageFooter>

        <ProdutoCad show={showCadProduto} onHide={() => { setShowCadProduto(false); setProdutoInicial(null); }} initialProduto={produtoInicial} onSaveProduto={produtoCadastrado} />

        {vinculoModal && <div className="modal show d-block importar-xml-modal" tabIndex="-1" role="dialog" aria-modal="true"><div className="modal-backdrop show" /><div className="modal-dialog modal-lg modal-dialog-centered"><div className="modal-content"><div className="modal-header"><div><h5 className="modal-title">{vinculoModal.modo === 'resolver' ? 'Resolver conflito de vínculo' : 'Vincular produto existente'}</h5><small>Código {vinculoModal.cProdFornec} · {fornecedor?.pessoa?.nome}</small></div><button type="button" className="btn-close" onClick={() => setVinculoModal(null)} /></div><div className="modal-body">{vinculoModal.modo === 'resolver' && <div className="alert alert-warning"><strong>Este código aponta para produtos diferentes.</strong><br />Escolha o vínculo correto; os demais serão removidos após sua confirmação.</div>}{vinculoModal.candidatos?.length > 0 && <div className="importar-xml-candidatos"><span>Vínculos encontrados</span>{vinculoModal.candidatos.map((candidato) => <button key={candidato.produtoId} type="button" className={`btn ${Number(vinculoModal.produtoId) === candidato.produtoId ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setVinculoModal((atual) => ({ ...atual, produtoId: candidato.produtoId, produto: candidato }))}>{candidato.produtoId} - {candidato.produtoDescricao || 'Produto sem descrição'}</button>)}</div>}<div className="importar-xml-modal-produto"><Input label="Produto *" size="100%" value={vinculoModal.produtoId || ''} onChange={(e) => setVinculoModal((atual) => ({ ...atual, produtoId: e.target.value, produto: null }))} btnBuscar={buscarProdutoVinculo} /><Input label="Descrição" size="100%" value={vinculoModal.produto?.descricao || vinculoModal.produto?.produtoDescricao || ''} disabled /></div></div><div className="modal-footer"><button className="btn btn-outline-secondary" onClick={() => setVinculoModal(null)}>Cancelar</button><button className="btn btn-success" disabled={!vinculoModal.produtoId} onClick={confirmarVinculo}><FaCheck /> Confirmar vínculo</button></div></div></div></div>}
      </div>
    </PageContainer>
  );
};

export default ImportarXMLPage;
